import React from 'react';

// Queue Rollbar reports until window.Rollbar (initialised asynchronously by
// the Stimulus rollbar controller) becomes available. If Rollbar never loads
// (e.g. in development without a token), reports are dropped silently after
// the retry window; the console.error remains the visible signal.
const MAX_ROLLBAR_WAIT_MS = 5000;
const ROLLBAR_POLL_INTERVAL_MS = 200;

const reportToRollbar = (error, extra) => {
  const start = Date.now();

  const attempt = () => {
    if (typeof window === 'undefined') return;
    if (window.Rollbar && typeof window.Rollbar.error === 'function') {
      try {
        window.Rollbar.error(error, extra);
      } catch (_) {
        // Never let error reporting throw and mask the original bug.
      }
      return;
    }
    if (Date.now() - start >= MAX_ROLLBAR_WAIT_MS) return;
    setTimeout(attempt, ROLLBAR_POLL_INTERVAL_MS);
  };

  attempt();
};

/**
 * Universal React error boundary. Catches any render/lifecycle error thrown
 * anywhere in the wrapped tree and forwards it to Rollbar. Renders a minimal
 * fallback so the tab is not a blank white page (which is impossible for the
 * user to distinguish from a network stall).
 *
 * Non-React errors (async callbacks, event handlers, promise rejections) are
 * captured by Rollbar's own `captureUncaught` / `captureUnhandledRejections`
 * config in `RollbarHelper.js_config`.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Always log to console so devs on Mac see it even without Rollbar.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);

    reportToRollbar(error, {
      componentStack: errorInfo?.componentStack,
      boundary: this.props.name || 'ErrorBoundary'
    });
  }

  handleReload() {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReload);
      }
      return (
        <div
          className="d-flex min-vh-100 align-items-center justify-content-center bg-light p-4"
          data-testid="error-boundary-fallback"
        >
          <div className="text-center" style={{ maxWidth: '28rem' }}>
            <h1 className="h4 fw-bold mb-2">Đã xảy ra lỗi</h1>
            <p className="text-muted mb-3">
              Trang bị lỗi bất ngờ. Chúng tôi đã ghi nhận và sẽ khắc phục sớm.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
            >
              Tải lại trang
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
